import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDeploymentsStore } from '@/stores/deploymentsStore';
import { useServicesStore } from '@/stores/servicesStore';
import { useEnvironmentsStore } from '@/stores/environmentsStore';
import { useTeamsStore } from '@/stores/teamsStore';
import { useUserTeamsStore } from '@/stores/userTeamsStore';
import { Deployment } from '@/types/deployment';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger, Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Checkbox, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Separator, Skeleton, Tabs, TabsContent, TabsList, TabsTrigger, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@nimbitlabs/ui';
import { 
  Search, 
  GitCommit, 
  Clock, 
  User, 
  CheckCircle, 
  XCircle, 
  Loader2,
  AlertTriangle,
  ExternalLink,
  Users,
  Package,
  FileText
} from 'lucide-react';

export const Deployments = () => {
  const { deployments, loading } = useDeploymentsStore();
  const { services } = useServicesStore();
  const { environments } = useEnvironmentsStore();
  const { teams: allTeams } = useTeamsStore();
  const { userTeamIds } = useUserTeamsStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [serviceFilter, setServiceFilter] = useState<string>('all');
  const [showMyTeamsOnly, setShowMyTeamsOnly] = useState(false);
  
  // Sort environments by tier (dev -> uat -> prod)
  const sortedEnvironments = [...environments].sort((a, b) => {
    const tierOrder = ['development', 'dev', 'staging', 'uat', 'qa', 'test', 'production', 'prod'];
    const getTier = (name: string) => {
      const lowerName = name.toLowerCase();
      const index = tierOrder.findIndex(tier => lowerName.includes(tier));
      return index === -1 ? 999 : index;
    };
    return getTier(a.name) - getTier(b.name);
  });

  console.log('🔍 [Deployments] Sorted environments:', sortedEnvironments.map(e => ({ id: e.id, name: e.name })));

  // Set selected tab to first environment or empty string
  const [selectedEnvironmentTab, setSelectedEnvironmentTab] = useState<string>('');

  console.log('🔍 [Deployments] Selected tab state:', selectedEnvironmentTab);

  // Environments are automatically fetched by useEnvironments hook

  // Update selected tab when environments load
  useEffect(() => {
    console.log('🔍 [Deployments] useEffect triggered:', {
      sortedEnvironmentsLength: sortedEnvironments.length,
      selectedEnvironmentTab,
      willUpdate: sortedEnvironments.length > 0 && !selectedEnvironmentTab
    });
    
    if (sortedEnvironments.length > 0 && !selectedEnvironmentTab) {
      console.log('✅ [Deployments] Setting selected tab to:', sortedEnvironments[0].id);
      setSelectedEnvironmentTab(sortedEnvironments[0].id);
    }
  }, [sortedEnvironments.length, selectedEnvironmentTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const getStatusIcon = (status: Deployment['status']) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'in_progress':
        return <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'pending':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: Deployment['status']) => {
    switch (status) {
      case 'success':
        return 'bg-green-500/10 text-green-700 border-green-200 dark:text-green-400 dark:border-green-800';
      case 'in_progress':
        return 'bg-blue-500/10 text-blue-700 border-blue-200 dark:text-blue-400 dark:border-blue-800';
      case 'failed':
        return 'bg-red-500/10 text-red-700 border-red-200 dark:text-red-400 dark:border-red-800';
      case 'pending':
        return 'bg-yellow-500/10 text-yellow-700 border-yellow-200 dark:text-yellow-400 dark:border-yellow-800';
      default:
        return 'bg-gray-500/10 text-gray-700 border-gray-200 dark:text-gray-400 dark:border-gray-800';
    }
  };

  const filteredDeployments = deployments.filter(deployment => {
    const service = services.find(s => s.id === deployment.serviceId);
    const matchesTeamFilter = !showMyTeamsOnly || (service && userTeamIds.includes(service.teamId));
    const matchesSearch = (deployment.serviceName?.toLowerCase() ?? '').includes(searchQuery.toLowerCase()) ||
                         (deployment.version?.toLowerCase() ?? '').includes(searchQuery.toLowerCase()) ||
                         (deployment.environmentName?.toLowerCase() ?? '').includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || deployment.status === statusFilter;
    const matchesService = serviceFilter === 'all' || deployment.serviceId === serviceFilter;
    
    return matchesTeamFilter && matchesSearch && matchesStatus && matchesService;
  });

  console.log('🔍 [Deployments] Filtered deployments:', {
    total: deployments.length,
    filtered: filteredDeployments.length,
    filters: { searchQuery, statusFilter, serviceFilter, showMyTeamsOnly }
  });

  // Group deployments by team, then by service
  const deploymentsByTeam = filteredDeployments.reduce((teamAcc, deployment) => {
    const service = services.find(s => s.id === deployment.serviceId);
    const teamId = service?.teamId;
    const teamName = service?.team;
    
    // Skip deployments without a valid team
    if (!teamId || !teamName) {
      return teamAcc;
    }
    
    if (!teamAcc[teamId]) {
      teamAcc[teamId] = {
        teamName,
        services: {}
      };
    }
    
    if (!teamAcc[teamId].services[deployment.serviceId]) {
      teamAcc[teamId].services[deployment.serviceId] = {
        serviceName: deployment.serviceName,
        deployments: []
      };
    }
    
    teamAcc[teamId].services[deployment.serviceId].deployments.push(deployment);
    return teamAcc;
  }, {} as Record<string, { 
    teamName: string; 
    services: Record<string, { serviceName: string; deployments: Deployment[] }> 
  }>);

  return (
    <div className="space-y-3 md:space-y-6 px-2 md:px-0">
      <div className="flex flex-col gap-3 md:flex-row md:justify-between md:items-start">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Deployments</h1>
          <p className="text-sm md:text-base text-muted-foreground">
            Track service versions across environments
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-2 md:pb-6">
          <CardTitle className="text-base md:text-lg">Filters</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 md:space-y-4">
          <div className="space-y-3 md:space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="my-teams-only-deployments" 
                checked={showMyTeamsOnly}
                onCheckedChange={(checked) => setShowMyTeamsOnly(checked === true)}
              />
              <label 
                htmlFor="my-teams-only-deployments" 
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Show only services from my teams
              </label>
            </div>
            <div className="flex flex-col gap-3 md:grid md:grid-cols-3 md:gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Search deployments..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="success">Success</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
              </SelectContent>
            </Select>

            <Select value={serviceFilter} onValueChange={setServiceFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by service" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Services</SelectItem>
                {services.map((service) => (
                  <SelectItem key={service.id} value={service.id}>
                    {service.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Environment Tabs - Only show when environments are loaded */}
      {sortedEnvironments.length > 0 && (
        <Tabs value={selectedEnvironmentTab} onValueChange={setSelectedEnvironmentTab} className="space-y-3 md:space-y-6">
          <div className="w-full overflow-x-auto -mx-2 px-2 md:mx-0 md:px-0">
            <div className="inline-flex min-w-full md:min-w-0 rounded-xl bg-card shadow-soft p-1">
              <TabsList className="inline-flex h-10 md:h-12 items-center justify-start gap-1 md:gap-2 rounded-xl bg-gradient-to-r from-muted/40 via-muted/30 to-muted/40 p-1 md:p-1.5 backdrop-blur-sm w-full">
                {sortedEnvironments.map((env) => (
                <TabsTrigger 
                key={env.id} 
                value={env.id}
                className="relative overflow-hidden data-[state=active]:text-primary data-[state=active]:scale-[1.05] md:data-[state=active]:scale-[1.08] data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:bg-muted/40 data-[state=inactive]:hover:text-foreground data-[state=inactive]:hover:scale-[1.02] px-3 md:px-6 py-2 md:py-2.5 rounded-lg font-bold text-xs md:text-sm transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] before:absolute before:inset-0 before:bg-primary/10 before:rounded-lg before:scale-75 before:opacity-0 before:rotate-[-2deg] data-[state=active]:before:scale-100 data-[state=active]:before:opacity-100 data-[state=active]:before:rotate-0 before:transition-all before:duration-500 before:ease-[cubic-bezier(0.34,1.56,0.64,1)] after:absolute after:inset-0 after:shadow-[0_0_20px_rgba(var(--primary-rgb),0.3)] after:rounded-lg after:scale-75 after:opacity-0 data-[state=active]:after:scale-100 data-[state=active]:after:opacity-100 after:transition-all after:duration-500 after:ease-[cubic-bezier(0.34,1.56,0.64,1)] whitespace-nowrap"
              >
                <span className="relative z-10">{env.name}</span>
              </TabsTrigger>
              ))}
            </TabsList>
          </div>
        </div>

        {sortedEnvironments.map((env) => {
          const envDeployments = filteredDeployments.filter(d => d.environmentId === env.id);
          console.log(`🔍 [Deployments] TabsContent for ${env.name}:`, {
            envId: env.id,
            isSelected: selectedEnvironmentTab === env.id,
            deploymentsCount: envDeployments.length,
            loading
          });
          
          return (
          <TabsContent key={env.id} value={env.id} className="space-y-3 md:space-y-4">
            {loading ? (
              <div className="space-y-4">
                {/* Skeleton for accordion teams */}
                {[1, 2, 3].map((teamIndex) => (
                  <Card key={teamIndex} className="shadow-soft">
                    <div className="px-6 py-4 space-y-3">
                      {/* Team header skeleton */}
                      <div className="flex items-center gap-3">
                        <Skeleton className="h-5 w-5 rounded" />
                        <div className="flex-1 space-y-2">
                          <Skeleton className="h-5 w-48" />
                          <Skeleton className="h-4 w-32" />
                        </div>
                      </div>
                      
                      {/* Service rows skeleton */}
                      <div className="border border-muted rounded-lg px-6 py-5 space-y-3">
                        {[1, 2].map((serviceIndex) => (
                          <div key={serviceIndex} className="flex items-stretch gap-4 py-2">
                            {/* Service name skeleton */}
                            <div className="flex items-center gap-3 bg-muted/50 rounded-lg px-4 py-2.5 w-full md:w-1/3">
                              <Skeleton className="h-4 w-4 rounded" />
                              <Skeleton className="h-4 flex-1" />
                              <Skeleton className="h-7 w-7 rounded" />
                            </div>
                            
                            {/* Deployment versions skeleton */}
                            <div className="flex gap-2 flex-1">
                              {[1, 2, 3].map((versionIndex) => (
                                <div key={versionIndex} className="flex flex-col items-center gap-1.5 bg-muted/30 rounded-lg p-2 border border-muted min-w-[100px]">
                                  <Skeleton className="h-5 w-16 rounded" />
                                  <Skeleton className="h-4 w-4 rounded-full" />
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
            <Accordion type="multiple" className="space-y-4">
              {Object.entries(deploymentsByTeam).map(([teamId, { teamName, services }]) => {
                const totalDeployments = Object.values(services).reduce((acc, service) => acc + service.deployments.length, 0);
                
                return (
                  <AccordionItem key={teamId} value={teamId} className="rounded-lg bg-card shadow-soft">
                    <AccordionTrigger className="px-6 py-4 hover:no-underline">
                      <div className="flex items-center justify-between w-full">
                        <div className="flex items-center gap-3">
                          <Users className="h-5 w-5 text-primary" />
                          <div className="text-left">
                            <h3 className="text-lg font-semibold">{teamName}</h3>
                            <p className="text-sm text-muted-foreground">
                              {Object.keys(services).length} service{Object.keys(services).length !== 1 ? 's' : ''} • {totalDeployments} deployment{totalDeployments !== 1 ? 's' : ''}
                            </p>
                          </div>
                        </div>
                      </div>
                    </AccordionTrigger>
                    
                    <AccordionContent className="px-6 pb-6 pt-2">
                      <div className="border border-muted rounded-lg">
                        <div className="px-6 py-5">
                          <div className="space-y-3">
                            {/* Service rows */}
                            {Object.entries(services).map(([serviceId, { serviceName, deployments }]) => {
                              // Get latest 5 deployments for this service/environment
                              const envDeployments = deployments
                                .filter(d => d.serviceId === serviceId && d.environmentId === env.id)
                                .sort((a, b) => new Date(b.deployedAt).getTime() - new Date(a.deployedAt).getTime()) // Get newest first
                                .slice(0, 5) // Take the 5 most recent
                                .reverse(); // Reverse to show oldest to newest (left to right)
                              
                              const getVersionBadgeColor = (status: Deployment['status']) => {
                                switch (status) {
                                  case 'success':
                                    return 'bg-green-500/15 text-green-700 border-green-300 dark:text-green-400 dark:border-green-700';
                                  case 'failed':
                                    return 'bg-red-500/15 text-red-700 border-red-300 dark:text-red-400 dark:border-red-700';
                                  case 'in_progress':
                                    return 'bg-blue-500/15 text-blue-700 border-blue-300 dark:text-blue-400 dark:border-blue-700';
                                  case 'pending':
                                    return 'bg-orange-500/15 text-orange-700 border-orange-300 dark:text-orange-400 dark:border-orange-700';
                                  default:
                                    return 'bg-muted/40 text-muted-foreground border-muted';
                                }
                              };
                              
                              return (
                                <div key={serviceId} className="flex flex-col md:flex-row md:items-stretch gap-2 md:gap-4 py-2">
                                  {/* Service name column */}
                                  <div className="flex items-center gap-2 md:gap-3 bg-muted/50 rounded-lg px-3 md:px-4 py-2 md:py-2.5 w-full md:w-1/3">
                                    <Package className="h-4 w-4 text-primary flex-shrink-0" />
                                    <span className="font-semibold text-xs md:text-sm flex-1 text-foreground truncate">{serviceName}</span>
                                    <Button variant="ghost" size="sm" asChild className="h-7 w-7 p-0 hover:bg-primary/10">
                                      <Link to={`/services/${serviceId}`}>
                                        <ExternalLink className="h-3.5 w-3.5" />
                                      </Link>
                                    </Button>
                                  </div>
                                  
                                  {/* Deployment versions */}
                                  <div className="flex items-stretch flex-1 overflow-x-auto -mx-2 px-2 md:mx-0 md:px-0">
                                    {envDeployments.length > 0 ? (
                                      <div className="flex gap-2 w-full justify-start min-w-max md:min-w-0">
                                        {envDeployments.map((envDeployment) => (
                                           <TooltipProvider key={envDeployment.id}>
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                <div className="flex flex-col items-center gap-1.5 cursor-pointer bg-muted/30 rounded-lg p-2 hover:bg-muted/50 transition-colors border border-muted min-w-[100px]">
                                                  <Badge 
                                                    variant="secondary" 
                                                    className={`text-xs font-mono font-semibold transition-colors ${getVersionBadgeColor(envDeployment.status)}`}
                                                  >
                                                    {envDeployment.version}
                                                  </Badge>
                                                  <div className="flex items-center justify-center">
                                                    {getStatusIcon(envDeployment.status)}
                                                  </div>
                                                </div>
                                              </TooltipTrigger>
                                              <TooltipContent side="top" className="max-w-xs">
                                                <div className="space-y-2 text-sm">
                                                  <div className="flex items-center gap-2">
                                                    <Package className="h-3 w-3" />
                                                    <span className="font-medium">Version:</span>
                                                    <span className="font-mono">{envDeployment.version}</span>
                                                  </div>
                                                  <div className="flex items-center gap-2">
                                                    <span className="font-medium">Status:</span>
                                                    <span>{envDeployment.status}</span>
                                                  </div>
                                                  {envDeployment.commitHash && (
                                                    <div className="flex items-center gap-2">
                                                      <GitCommit className="h-3 w-3" />
                                                      <span className="font-medium">Commit:</span>
                                                      <span className="font-mono">{envDeployment.commitHash}</span>
                                                    </div>
                                                  )}
                                                  {envDeployment.deployedBy && (
                                                    <div className="flex items-center gap-2">
                                                      <User className="h-3 w-3" />
                                                      <span className="font-medium">Deployed by:</span>
                                                      <span>{envDeployment.deployedBy}</span>
                                                    </div>
                                                  )}
                                                  {envDeployment.deployedAt && (
                                                    <div className="flex items-center gap-2">
                                                      <Clock className="h-3 w-3" />
                                                      <span className="font-medium">Deployed:</span>
                                                      <span>{new Date(envDeployment.deployedAt).toLocaleString()}</span>
                                                    </div>
                                                  )}
                                                  {envDeployment.releaseNotes && (
                                                    <div className="flex items-start gap-2 pt-1 border-t">
                                                      <FileText className="h-3 w-3 mt-0.5" />
                                                      <div>
                                                        <span className="font-medium block">Release Notes:</span>
                                                        <span className="text-muted-foreground">{envDeployment.releaseNotes}</span>
                                                      </div>
                                                    </div>
                                                  )}
                                                </div>
                                              </TooltipContent>
                                            </Tooltip>
                                          </TooltipProvider>
                                        ))}
                                      </div>
                                    ) : (
                                      <div className="text-center space-y-2 bg-muted/20 border border-dashed border-muted-foreground/30 rounded-lg p-3 w-full">
                                        <Badge variant="outline" className="text-xs text-muted-foreground border-dashed">
                                          Not deployed
                                        </Badge>
                                        <div className="flex items-center justify-center pt-1">
                                          <div className="h-4 w-4 rounded-full border-2 border-dashed border-muted-foreground/30"></div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
            )}
          </TabsContent>
          );
        })}
        </Tabs>
      )}

      {filteredDeployments.length === 0 && (
          <Card>
            <CardContent className="text-center py-12">
              <div className="text-muted-foreground">
                <GitCommit className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p className="text-lg font-medium mb-2">No deployments found</p>
                <p>Try adjusting your filters or deploy a service to get started.</p>
              </div>
            </CardContent>
        </Card>
      )}
    </div>
  );
};
